import FormContainer from "@client/content/controls/Form/container";
import { getRootContainerProps } from "@client/content/lib/helper";
import { type IRef, withOnSubmit } from "@client/content/modules/Checkout/hooks";
import { type FormFieldInfo, type FormHandles } from "@client/controls/Form/component";
import { unwrapResponse } from "@client/lib/api";
import { getGoogleFontFromNames, isGoogleFontLabel } from "@client/lib/fonts";
import { type GenericMap } from "@client/types";
import { getStripeInputStyle } from "@common/components/Form/Field/StripeCard/lib";
import { CreditCardInputContainer } from "@common/controls/Inputs/CreditCard/component";
import { useI18n } from "@common/hooks/i18n";
import Deferred from "@common/lib/deferred";
import { usePromise, useUpdated } from "@common/lib/hooks";
import { toKebab } from "@common/lib/object";
import classNames from "classnames";
import * as React from "react";
import api from "../../../api";
import { type Checkout } from "../../../types";
import { type PaymentMethodBaseProps } from "../component";
import { adjustHeightValue, getError, init, INPUT_CLASSES } from "./lib";
import { type CollectJsCallbackArgs } from "./types";

export type Props = PaymentMethodBaseProps & {
  amount: number;
  currencyCode: string;
  parameters: Checkout.IntegrationParameter[];
};

const CheckoutPaymentNmiCreditCard = (props: Props) => {
  const i18n = useI18n();

  const ref = withOnSubmit<FormHandles>((data) => {
    return {
      paymentInfo: {
        ...data,
        type: "Nmi"
      }
    };
  });
  const id = React.useMemo(() => "container" + props.id.replaceAll("-", "").toLowerCase(), [props.id]);

  const [validations, setValidations] = React.useState<GenericMap<string | null>>({});

  const tokenizationKey = React.useMemo(
    () => props.parameters.find((x) => x.name === "NMITokenizationKey")?.value,
    [props.parameters]
  );

  const [collectJs, isLoadingCollectJs] = usePromise({
    fn: init,
    args: [tokenizationKey],
    shouldLoad: !!tokenizationKey
  });

  const [error, setError] = React.useState<string | null>();
  const [isInitializing, setIsInitializing] = React.useState<boolean>(isLoadingCollectJs);

  const callbackDeferred = React.useRef(new Deferred<CollectJsCallbackArgs>());

  React.useImperativeHandle<any, IRef>(
    ref,
    () => ({
      getFinalValues: async () => {
        collectJs?.startPaymentRequest();

        const result = await callbackDeferred.current.promise;
        const res = await api.payment(props.apiInfo, {
          paymentMethodId: props.paymentMethodId!,
          paymentToken: result.token,
          billingShipingInfo: props.billingShipingInfo
        });
        if (res.error) {
          setError(res.error);
          throw new Error(res.error);
        }

        const paymentResponse = unwrapResponse(res);
        return {
          customerId: paymentResponse.customerId,
          paymentHint: result.card.number,
          paymentToken: paymentResponse.id
        };
      },
      validate: async () => {
        const error = getError(i18n, validations);
        setError(error);
        return error;
      }
    }),
    [ref, validations, collectJs?.startPaymentRequest, props.billingShipingInfo, props.apiInfo, props.paymentMethodId]
  );

  const fields = React.useMemo(
    (): FormFieldInfo[] => [
      {
        isPaddingOnContainer: true,
        name: "paymentToken",
        Component: CreditCardInputContainer,
        componentOpts: {
          classes: INPUT_CLASSES,
          isLoading: isInitializing,
          preloaderStyle: {
            fontSize: props.fieldStyle.fontSize,
            lineHeight: props.fieldStyle.lineHeight,
            minHeight: adjustHeightValue(props.fieldStyle.lineHeight),
            "--height": adjustHeightValue(props.fieldStyle.lineHeight)
          }
        },
        error,
        defaultValue: "dummy"
      }
    ],
    [error, isInitializing, props.fieldStyle]
  );

  useUpdated(() => {
    if (!collectJs) return;

    const getSelector = (className: string) => `.${id} .${className}`;

    const style = getStripeInputStyle(props.fieldStyle);

    collectJs.configure({
      callback: (args: CollectJsCallbackArgs) => {
        callbackDeferred.current.resolve(args);
      },
      currency: props.currencyCode,
      customCss: {
        backgroundColor: "transparent",
        border: "none",
        ...toKebab(style.input, (x) => x?.toString())
      },
      fields: {
        ccexp: {
          placeholder: "MM/YY",
          selector: getSelector(INPUT_CLASSES.date)
        },
        ccnumber: {
          placeholder: i18n.cardNumber,
          selector: getSelector(INPUT_CLASSES.number)
        },
        cvv: {
          placeholder: "CVV",
          selector: getSelector(INPUT_CLASSES.cvv)
        }
      },
      fieldsAvailableCallback: () => setIsInitializing(false),
      focusCss: {
        "outline-width": "0px"
      },
      googleFont:
        props.fieldStyle.fontFamily && isGoogleFontLabel(props.fieldStyle.fontFamily)
          ? getGoogleFontFromNames([props.fieldStyle.fontFamily])
          : undefined,
      placeholderCss: toKebab(style.placeholder, (x) => x?.toString()),
      price: props.amount,
      styleSniffer: false,
      validationCallback: (target: string, isValid: boolean, message: string) => {
        setValidations((x) => ({ ...x, [target]: isValid ? null : message }));
      },
      variant: "inline"
    });
  }, [id, collectJs, props.amount, props.currencyCode, props.fieldStyle]);

  return (
    <FormContainer
      {...getRootContainerProps(props)}
      className={classNames(id, "nmi")}
      fields={fields}
      validateOn="change"
    />
  );
};

export default CheckoutPaymentNmiCreditCard;
